import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ticketsApi } from '@/api/tickets.api';
import { TicketTable } from '@/components/tickets/TicketTable';
import { TicketFiltersBar } from '@/components/tickets/TicketFiltersBar';
import { LoadingState } from '@/components/ui/LoadingState';
import { ErrorState } from '@/components/ui/ErrorState';
import { useRealtime } from '@/context/RealtimeContext';
import { useFiltersInUrl } from '@/hooks/useFiltersInUrl';

export function TicketsListPage() {
  // V4.9.5+ — URL ↔ state sync centralised in useFiltersInUrl so every
  // ticket-list page (All Tickets, My Queue, Approvals, Archives, Deleted)
  // round-trips filters/sort/page through the URL. Back from /tickets/:id
  // now restores the exact view the user was looking at.
  const [query, setQuery] = useFiltersInUrl();
  const queryClient = useQueryClient();
  const { on } = useRealtime();

  // V4.9.5 — Live updates for the All Tickets list. The instant ANY user
  // (anywhere in the world, on any role) creates a ticket, changes its
  // status, reassigns, or otherwise mutates it — every other open All
  // Tickets tab refetches and the new/changed row appears. No "refresh"
  // button click needed.
  //
  // The backend publishes ticket.created as a GLOBAL broadcast (no
  // ownerId filter, V4.9.5 fix in tickets.service.ts), so this listener
  // fires on every connected client for every new ticket regardless of
  // who created it or who it was assigned to.
  useEffect(() => {
    const invalidate = () => {
      void queryClient.invalidateQueries({ queryKey: ['tickets'] });
    };
    const off1 = on('ticket.created', invalidate);
    const off2 = on('ticket.status_changed', invalidate);
    const off3 = on('ticket.updated', invalidate);
    const off4 = on('ticket.approval_decided', invalidate);
    const off5 = on('ticket.cx_informed', invalidate);
    return () => {
      off1();
      off2();
      off3();
      off4();
      off5();
    };
  }, [on, queryClient]);

  const { data, isLoading, isError, error, refetch, isFetching } = useQuery({
    queryKey: ['tickets', query],
    queryFn: () => ticketsApi.list(query),
    placeholderData: (prev) => prev
  });

  // V4.9.2 — Refresh now does a HARD window reload (per ops request).
  // Soft refetch wasn't reloading static assets, so visual updates after
  // a deploy didn't reflect until the user manually Ctrl+Shift+R'd.
  // Hard reload guarantees they always see the latest UI + data.
  const handleRefresh = () => {
    window.location.reload();
  };

  const total = data?.pagination.total ?? 0;
  const page = data?.pagination.page ?? 1;
  const perPage = data?.pagination.per_page ?? 25;
  const totalPages = data?.pagination.total_pages ?? 1;

  return (
    <div>
      <div className="section-header">
        <h2>Tickets {total > 0 ? <span className="text-muted text-small">· {total}</span> : null}</h2>
        <div className="flex gap-2">
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            onClick={handleRefresh}
            disabled={isFetching}
          >
            {isFetching ? 'Refreshing...' : 'Refresh'}
          </button>
          <Link to="/tickets/new" className="btn btn-primary btn-sm">
            New ticket
          </Link>
        </div>
      </div>

      {/* V4.9.2 — `card-overflow-visible` lets the MultiSelect dropdowns
          (Status, Category, Store, Severity) extend below the card without
          getting clipped by the card's default `overflow: hidden`. */}
      <div className="card card-overflow-visible">
        <TicketFiltersBar
          value={query}
          onChange={(next) => {
            // V4.9.5+ — just call setQuery. The state → URL effect above
            // mirrors every change into searchParams automatically, so the
            // back button from /tickets/:id restores the exact filter,
            // sort, AND page the user was on.
            setQuery(next);
          }}
        />

        {isLoading ? (
          <LoadingState label="Loading tickets..." />
        ) : isError ? (
          <ErrorState error={error} onRetry={() => refetch()} />
        ) : (
          <>
            <TicketTable tickets={data?.data ?? []} />
            <div className="pagination">
              <div className="text-small text-muted">
                Page {page} of {totalPages} · {total} tickets
              </div>
              <div className="flex gap-2">
                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  disabled={page <= 1}
                  onClick={() => setQuery((prev) => ({ ...prev, page: (prev.page ?? 1) - 1 }))}
                >
                  Previous
                </button>
                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  disabled={page >= totalPages}
                  onClick={() => setQuery((prev) => ({ ...prev, page: (prev.page ?? 1) + 1 }))}
                >
                  Next
                </button>
                <select
                  className="form-select"
                  style={{ width: 'auto' }}
                  value={perPage}
                  onChange={(e) =>
                    setQuery((prev) => ({
                      ...prev,
                      per_page: Number(e.target.value),
                      page: 1
                    }))
                  }
                >
                  {[10, 25, 50, 100].map((n) => (
                    <option key={n} value={n}>
                      {n} / page
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
